import { useMemo, useState } from 'react'
import { autoDots, computeDots, dotsFromScores } from './ggDots.js'
import { samplePlayers, samplePars } from './ggSamplePlayers.js'

// G-Dots — isolated sandbox. Auto-settle (ON): score-derived dots (birdies) +
// one-tap junk (greenie / sandy / closest-to-pin), combined tally × $dotValue.
// Manual (OFF): hand-enter each player's dot count.
const cell = { border: '1px solid #ddd', padding: '6px 10px', fontSize: 16 }
const JUNK = ['greenie', 'sandy', 'cth']
const Seg = ({ options, value, onChange }) => (
  <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
    {options.map((o) => {
      const on = value === o.value
      return <button key={String(o.value)} onClick={() => onChange(o.value)} className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
        style={on ? { backgroundColor: '#1B4D2E', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>{o.label}</button>
    })}
  </div>
)

export default function GDots() {
  const players = useMemo(() => samplePlayers(), [])
  const pars = useMemo(() => samplePars(), [])
  const [auto, setAuto] = useState(true)
  const [dotValue, setDotValue] = useState(1)
  const [junk, setJunk] = useState([]) // [{ type, playerId }]
  const [addPid, setAddPid] = useState(players[0]?.id || '')
  const [addType, setAddType] = useState('greenie')
  const [manual, setManual] = useState({}) // { pid: count }

  const rows = useMemo(
    () => (auto ? autoDots(players, pars, junk, Number(dotValue) || 0) : computeDots(players, manual, Number(dotValue) || 0)),
    [auto, players, pars, junk, dotValue, manual],
  )
  const scoreDots = useMemo(() => dotsFromScores(players, pars), [players, pars])

  return (
    <div className="card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Seg options={[{ value: true, label: 'Auto-settle' }, { value: false, label: 'Manual' }]} value={auto} onChange={setAuto} />
        <span className="text-sm text-gray-600 font-semibold">Dot $<input type="number" className="w-14 border rounded px-1 py-0.5 ml-1" value={dotValue} onChange={(e) => setDotValue(e.target.value)} /></span>
      </div>

      {auto ? (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
          <span className="text-gray-500">Add junk dot:</span>
          <select className="border rounded px-1 py-0.5" value={addPid} onChange={(e) => setAddPid(e.target.value)}>{players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <select className="border rounded px-1 py-0.5" value={addType} onChange={(e) => setAddType(e.target.value)}>{JUNK.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          <button className="btn-ghost" onClick={() => setJunk((j) => [...j, { type: addType, playerId: addPid }])}>+ Add</button>
          {junk.length > 0 && <button className="btn-ghost text-gray-500" onClick={() => setJunk([])}>clear ({junk.length})</button>}
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-2">Enter each player's dot count.</p>
      )}

      <table className="w-full text-base" style={{ borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>{['Player', auto ? 'Birdie dots' : 'Dots', 'Total dots', '$'].map((h) => <th key={h} style={{ ...cell, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((p) => (
          <tr key={p.id}>
            <td style={cell}>{p.name}</td>
            <td style={cell}>{auto ? (scoreDots[p.id] || 0) : (
              <input type="number" className="w-14 border rounded px-1 py-0.5" value={manual[p.id] ?? ''} onChange={(e) => setManual((m) => ({ ...m, [p.id]: Number(e.target.value) || 0 }))} />
            )}</td>
            <td style={cell}><b>{p.dots}</b></td>
            <td style={cell}>${p.value}</td>
          </tr>
        ))}</tbody>
      </table>
      <p className="text-sm text-gray-500 mt-2">{auto ? 'Birdies auto-counted from the scorecard; add greenies/sandies/CTH with one tap.' : 'Manual — track dots yourself; auto-settle stays available as a toggle.'}</p>
    </div>
  )
}
